/**
 * useGlobalSearchKeyboard — V107.52.34
 *
 * Listens for Cmd+K / Ctrl+K / "/" to open the global search modal.
 * Bails when focus is in any text input so users typing a slash in a
 * textarea / contenteditable get a literal slash, not the search modal.
 *
 * "/" is also left alone (docs/proposal-v2/R7.md §3.4, item 4 — "$160 / month"
 * opened search and lost the text):
 *   - anywhere inside the Proposal V2 builder page (`[data-builder-v2]`) — its
 *     resting fields are buttons that open on a typed key, so "/" is text there;
 *   - when a handler already claimed the key (`defaultPrevented`) — a resting
 *     builder field that opened on it has swapped itself for a text box by the
 *     time the event bubbles here, so its original target is detached.
 * Cmd+K / Ctrl+K is unchanged.
 *
 * Mounted once inside `GlobalSearchProvider`, which itself is mounted
 * inside OfficeLayout — so the shortcut is automatically scoped to
 * `/home/*` routes and won't fire on tech routes.
 */

import { useEffect } from 'react';

interface Options {
  onOpen: () => void;
  /** Whether the modal is currently open — affects whether we steal Esc */
  isOpen: boolean;
  onClose: () => void;
}

/** The Proposal V2 builder page root (`ProposalBuilderV2Page`). */
const BUILDER_V2_ROOT = '[data-builder-v2]';

export function useGlobalSearchKeyboard({ onOpen, isOpen, onClose }: Options) {
  useEffect(() => {
    function isTypingTarget(t: EventTarget | null): boolean {
      if (!t || !(t instanceof HTMLElement)) return false;
      const tag = t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (t.isContentEditable) return true;
      return false;
    }

    function insideBuilderV2(t: EventTarget | null): boolean {
      return t instanceof Element && t.closest(BUILDER_V2_ROOT) !== null;
    }

    function handler(e: KeyboardEvent) {
      // Esc closes the modal when open. We don't read isOpen here directly
      // (effect deps would change every render); the parent passes the
      // current onClose closure which already references state.
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        onClose();
        return;
      }

      // Cmd+K / Ctrl+K opens the modal — works even from inside an input.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpen();
        return;
      }

      // "/" opens the modal, but only when focus is NOT in a text input, not in
      // the V2 proposal builder, and no handler has already used the key.
      if (e.key === '/' && !e.defaultPrevented && !isTypingTarget(e.target) && !insideBuilderV2(e.target)) {
        e.preventDefault();
        onOpen();
        return;
      }
    }

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onOpen, onClose, isOpen]);
}
